import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import _ from "lodash";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useFeatures } from "flagged";

import MasterLayout from "@layouts/MasterLayout.react";
import { Div, Image, Button } from "@components/Generics.react";
import { settings } from "@config/settings/app.settings";
import RecommendedArticles from "@components/RecommendedArticles.react";
import DeckToolbar from "@components/DeckToolbar.react";
import TermsPage from "./panels/TermsPage.react";
import ReactPlayer from "react-player/lazy";
import { Divider, Modal } from "semantic-ui-react";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function getMediaType(url = "") {
    const cleanUrl = url
        .split("?")[0]
        .split("#")[0]
        .toLowerCase();

    const lowerUrl = url.toLowerCase();

    if (
        lowerUrl.includes("youtube.com") ||
        lowerUrl.includes("youtu.be") ||
        lowerUrl.includes("vimeo.com") ||
        /\.(mp4|webm|ogg|mov|m4v|avi|mkv|flv)$/.test(cleanUrl)
    ) {
        return "video";
    }

    if (
        /\.(jpg|jpeg|png|gif|webp|svg|bmp|avif|jfif)$/.test(
            cleanUrl
        )
    ) {
        return "image";
    }

    if (/\.pdf$/.test(cleanUrl)) {
        return "pdf";
    }

    return "link";
}

function getYoutubeThumbnail(url = "") {
    try {
        const parsedUrl = new URL(url);
        let videoId = null;

        if (parsedUrl.hostname.includes("youtube.com")) {
            videoId = parsedUrl.searchParams.get("v");

            if (!videoId) {
                const embedMatch = parsedUrl.pathname.match(
                    /\/embed\/([^/]+)/
                );

                const shortsMatch = parsedUrl.pathname.match(
                    /\/shorts\/([^/]+)/
                );

                videoId =
                    embedMatch?.[1] ||
                    shortsMatch?.[1];
            }
        }

        if (parsedUrl.hostname === "youtu.be") {
            videoId = parsedUrl.pathname
                .replace("/", "")
                .split("/")[0];
        }

        return videoId
            ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
            : "";
    } catch (error) {
        return "";
    }
}

export default function DesktopHome(props) {
    const user = useSelector((state) => state.auth.user);
    const { t } = useTranslation("common");
    const features = useFeatures();
    const isPortrait = window.innerHeight > window.innerWidth;

    const [pdfOpen, setPdfOpen] = useState(false);
    const [pdfNumPages, setPdfNumPages] = useState(null);
    const [pdfPageNumber, setPdfPageNumber] = useState(0);
    const [pdfScale, setPdfScale] = useState(1);
    const pdfViewportRef = React.useRef(null);

    function goToPdfPage(index) {
        if (index < 0 || (pdfNumPages && index >= pdfNumPages)) return;
        setPdfPageNumber(index);
        const target = pdfViewportRef.current?.querySelectorAll(
            ".homepdf-page-wrap"
        )[index];
        if (target) {
            target.scrollIntoView({ behavior: "auto", block: "start" });
        }
    }

    const homePdf = _.get(features, "general.homePdf.enabled", false);
    const pdfUrl = _.get(features, "general.homePdf.url", "");
    const pdfThumb = _.get(
        features,
        "general.homePdf.image",
        "/assets/images/configurable/axisimages/completesim.jpeg"
    );

    const mediaType = getMediaType(pdfUrl);

    const mediaThumb =
        _.get(features, "general.homePdf.image", "") ||
        (mediaType === "video"
            ? getYoutubeThumbnail(pdfUrl)
            : "/assets/images/configurable/axisimages/completesim.jpeg");

    const stages = settings.gamification.stages;
    const userStage = settings.home[user.stage || stages.length];

    const pageContent =
        user.license.termsAccepted || !features.general.acceptTerms ? (
            <Div fullht fluid flex top-aligned>
                <Div wd="calc(100% - 360px)" padSides>
                    <Div fluid flex top-aligned>
                        <Div wd="40%" altText huge uppercase>
                            <Div wd="40%" altText huge uppercase txtCharcoal>
                                Welcome{" "}
                            </Div>
                            {user.branding?.name
                                ? "to " + user.branding?.name
                                : ""}
                        </Div>
                        <Div wd="60%">
                            {homePdf ? (
                                <>
                                    <Div
                                        rounded
                                        noOverflow
                                        gutter
                                        relative
                                        clickable
                                        className="video-responsive"
                                        onClick={() => {
                                            setPdfPageNumber(0);
                                            setPdfNumPages(null);
                                            setPdfOpen(true);
                                        }}
                                    >
                                        <Image
                                            fluid
                                            src={
                                                mediaType === "image"
                                                    ? pdfUrl
                                                    : mediaThumb
                                            }
                                            style={{
                                                position: "absolute",
                                                inset: 0,
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />

                                        {mediaType === "video" && (
                                            <Div
                                                absolute
                                                fluid
                                                fullht
                                                flex
                                                center-align
                                                bgColor="#00000033"
                                            >
                                                <Div
                                                    wd="70px"
                                                    ht="70px"
                                                    pureCircle
                                                    flex
                                                    center-align
                                                    bgColor="#000000bb"
                                                    txtColor="#ffffff"
                                                    huge
                                                >
                                                    ▶
                                                </Div>
                                            </Div>
                                        )}
                                    </Div>
                                    <Modal
                                        open={pdfOpen}
                                        onClose={() =>
                                            setPdfOpen(false)
                                        }
                                        size="large"
                                        closeIcon
                                    >
                                        <Div
                                            rounded
                                            noOverflow
                                            relative
                                            style={{
                                                maxHeight: "80vh",
                                                background: "#000",
                                            }}
                                        >
                                            {mediaType === "pdf" && (
                                                <>
                                                    <div
                                                        ref={pdfViewportRef}
                                                        style={{
                                                            width: "110%",
                                                            height: "80vh",
                                                            overflow: "auto",
                                                            background: "#000",
                                                        }}
                                                    >
                                                        <Document
                                                            file={pdfUrl}
                                                            onLoadSuccess={({
                                                                numPages,
                                                            }) =>
                                                                setPdfNumPages(numPages)
                                                            }
                                                            onContextMenu={(e) =>
                                                                e.preventDefault()
                                                            }
                                                        >
                                                            {_.map(
                                                                new Array(
                                                                    pdfNumPages || 0
                                                                ).fill({}),
                                                                (elm, pIdx) => (
                                                                    <div
                                                                        key={`homepdfpage-${pIdx}`}
                                                                        className="homepdf-page-wrap"
                                                                        style={{
                                                                            display:
                                                                                "flex",
                                                                            justifyContent:
                                                                                "center",
                                                                            background:
                                                                                "#000",
                                                                        }}
                                                                    >
                                                                        <Page
                                                                            pageNumber={
                                                                                pIdx + 1
                                                                            }
                                                                            scale={
                                                                                pdfScale
                                                                            }
                                                                            renderTextLayer={
                                                                                false
                                                                            }
                                                                            renderAnnotationLayer={
                                                                                false
                                                                            }
                                                                        />
                                                                    </div>
                                                                )
                                                            )}
                                                        </Document>
                                                    </div>
                                                    <DeckToolbar
                                                        {...{
                                                            deck: {
                                                                navEnabled: true,
                                                                jumpAhead: true,
                                                                content: _.times(
                                                                    pdfNumPages || 0,
                                                                    () => ({})
                                                                ),
                                                            },
                                                            scale: pdfScale,
                                                            setScale: setPdfScale,
                                                            pageNumber: pdfPageNumber,
                                                            setPageNumber: goToPdfPage,
                                                            numPages: pdfNumPages,
                                                            noFullScreen: true,
                                                        }}
                                                    />
                                                </>
                                            )}

                                            {mediaType === "image" && (
                                                <Div
                                                    fluid
                                                    ht="80vh"
                                                    flex
                                                    center-align
                                                    bgColor="#000"
                                                >
                                                    <img
                                                        src={pdfUrl}
                                                        alt=""
                                                        style={{
                                                            maxWidth: "100%",
                                                            maxHeight: "80vh",
                                                            objectFit:
                                                                "contain",
                                                        }}
                                                    />
                                                </Div>
                                            )}

                                            {mediaType === "video" && (
                                                <Div
                                                    fluid
                                                    ht="80vh"
                                                    bgColor="#000"
                                                >
                                                    <ReactPlayer
                                                        className="react-player"
                                                        url={pdfUrl}
                                                        width="100%"
                                                        height="100%"
                                                        controls
                                                        playing={pdfOpen}
                                                        playsinline
                                                        config={{
                                                            file: {
                                                                attributes: {
                                                                    controlsList: "nodownload",
                                                                    onContextMenu: (e) => e.preventDefault(),
                                                                },
                                                            },
                                                            youtube: {
                                                                playerVars: {
                                                                    rel: 0,
                                                                    modestbranding: 1,
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </Div>
                                            )}

                                            {mediaType === "link" && (
                                                <Div
                                                    fluid
                                                    ht="80vh"
                                                    flex
                                                    center-align
                                                    bgColor="#000"
                                                >
                                                    <a
                                                        href={pdfUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Open Content
                                                    </a>
                                                </Div>
                                            )}
                                        </Div>
                                        <Div
                                            fluid
                                            danger
                                            center-align
                                            large
                                            medpad
                                            clickable
                                            onClick={() => setPdfOpen(false)}
                                        >
                                            Close
                                        </Div>
                                    </Modal>
                                </>
                            ) : (
                                <Div
                                    rounded
                                    noOverflow
                                    gutter
                                    className="video-responsive"
                                >
                                    <ReactPlayer
                                        className="react-player"
                                        url={user.branding?.homeVideo}
                                        width="100%"
                                        height="100%"
                                    />
                                </Div>
                            )}
                        </Div>
                    </Div>
                    <Divider />
                    <br />
                    <Div ash basepad>
                        <Div altText large uppercase gutter>
                            How it works:
                        </Div>
                        <ul>
                            {_.map(
                                t("home.howItWorks", { returnObjects: true }),
                                (text, idx) => (
                                    <li key={`howitworks-${idx}`}>{text}</li>
                                )
                            )}
                        </ul>
                        {/* this is only for axis simulation */}
                        {/* <Button
                            primary
                            href="/library"
                            content="Start Simulation"
                        /> */}
                    </Div>
                </Div>
                <Div wd="360px" fullht basepad peat rounded>
                    <Div gutter big uppercase altText>
                        {/* Quick Links: */}
                        {/* this is only for axis simulation */}
                        Infobase
                    </Div>
                    <Div
                        relative
                        fluid
                        style={{ height: "0", paddingBottom: "133%" }}
                    >
                        {userStage &&
                            _.map(userStage.images, ({ src, coords }, idx) => {
                                return (
                                    <NavLink
                                        to={t(`home.links.${src}`)}
                                        key={`homeelm-${idx}`}
                                    >
                                        <Image
                                            src={t(`home.images.${src}`)}
                                            style={{
                                                position: "absolute",
                                                left: `${coords[0]}%`,
                                                top: `${coords[1]}%`,
                                                width: `${coords[2]}%`,
                                            }}
                                        />
                                    </NavLink>
                                );
                            })}
                    </Div>
                </Div>
            </Div>
        ) : (
            <TermsPage />
        );
    const mobileView = (
        <Div centered fluid fullht balanced>
            <Image
                src={user.branding?.homeImage || "/assets/images/configurable/lock.png"}
                fluid
            />
            <br />
            <Div altText massive gutter>Please Use a Desktop Device</Div>
            <Div altText large>
                This website is optimized for larger screens and may not work correctly on mobile devices.
                For the best experience, please view this site on a desktop or laptop computer.
            </Div>
        </Div>
    );
    // ========================= Render Function =================================
    return (
        <MasterLayout
            title={"Welcome"}
            mainPanel={!isPortrait ? pageContent : mobileView}
            rightPanelName={t(`components.recommended`)}
            rightPanel={<RecommendedArticles />}
            rootPage={true}
            hidePanelToggle
            startFullScreen={true}
        />
    );
}
